/**
 * The partner's sites and which one the portal is working on.
 *
 * `POST /sites` always creates a live/test PAIR; `GET /sites` lists both rows
 * flat. The pairing lives only on the TEST row (`paired_site_key` → live key),
 * so the provider reads each test row once to pair them. Everything
 * site-scoped in the UI (snippet, domains, verify, config) hangs off the LIVE
 * key, with its test twin one property away.
 */
import { createContext, useCallback, useContext, useEffect, useMemo, useState, type ReactNode } from 'react';
import { useSession } from '../auth/session';
import { can } from '../auth/roles';
import type { SiteSummary } from '../api/types';
import { LastSeenProvider } from './lastSeen';

export interface SitePair {
  live: SiteSummary;
  test: SiteSummary | null;
}

interface SitesValue {
  loading: boolean;
  pairs: SitePair[];
  current: SitePair | null;
  select: (liveKey: string) => void;
  reload: () => Promise<void>;
}

const SitesContext = createContext<SitesValue | null>(null);
const CURRENT_KEY = 'fx-partner-current-site';

export function SitesProvider({ children }: { children: ReactNode }) {
  const { api, role, org } = useSession();
  const [pairs, setPairs] = useState<SitePair[]>([]);
  const [loading, setLoading] = useState(true);
  const [currentKey, setCurrentKey] = useState<string | null>(() => {
    try {
      return window.localStorage.getItem(CURRENT_KEY);
    } catch {
      return null;
    }
  });
  const allowed = can(role, 'sites');

  const reload = useCallback(async () => {
    if (!allowed) {
      setPairs([]);
      setLoading(false);
      return;
    }
    try {
      const { items } = await api.listSites();
      const live = items.filter((s) => s.environment === 'live');
      const tests = items.filter((s) => s.environment === 'test');
      const details = await Promise.all(tests.map((t) => api.getSite(t.site_key).catch(() => null)));
      const byLive = new Map<string, SiteSummary>();
      details.forEach((d, i) => {
        if (d?.paired_site_key) byLive.set(d.paired_site_key, tests[i] as SiteSummary);
      });
      setPairs(live.map((l) => ({ live: l, test: byLive.get(l.site_key) ?? null })));
    } finally {
      setLoading(false);
    }
  }, [api, allowed]);

  useEffect(() => {
    void reload();
  }, [reload, org?.id]);

  const select = useCallback((liveKey: string) => {
    setCurrentKey(liveKey);
    try {
      window.localStorage.setItem(CURRENT_KEY, liveKey);
    } catch {
      /* ignore */
    }
  }, []);

  const current = useMemo(
    () => pairs.find((p) => p.live.site_key === currentKey) ?? pairs[0] ?? null,
    [pairs, currentKey],
  );

  return (
    <SitesContext.Provider value={{ loading, pairs, current, select, reload }}>
      <LastSeenProvider>{children}</LastSeenProvider>
    </SitesContext.Provider>
  );
}

export function useSites(): SitesValue {
  const ctx = useContext(SitesContext);
  if (!ctx) throw new Error('useSites outside SitesProvider');
  return ctx;
}
